import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { LanguageToggle } from '../LanguageToggle'
import { useLocale } from '../../contexts/LocaleContext'
import { company } from '../../legal/company'
import { LEGAL_SLUGS, legalDocument, legalUpdated, type LegalSlug } from '../../legal/documents'

/**
 * The public legal section (spec 2026-09-09-02).
 *
 * Served to someone OUTSIDE any tenant — a resident who followed a link from a
 * WhatsApp message, a Meta reviewer checking the privacy policy of the app, a
 * prospect reading the terms — so it renders under the root's BareShell, with
 * no sidebar, no command palette and no organization. The only chrome is the
 * product name, a language toggle and the sibling documents.
 *
 * The documents themselves are Markdown (see ../../legal), but this chrome is
 * UI copy like any other and lives in the `legal` i18n namespace.
 */
function LegalLayout({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation('legal')

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="legal-page">
            <header className="border-b bg-background">
                <div className="mx-auto max-w-3xl px-4 md:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/about" className="flex flex-col min-w-0" data-testid="legal-home-link">
                        <span className="text-base font-semibold text-foreground truncate">
                            {company.product}
                        </span>
                        <span className="text-xs text-muted-foreground truncate">
                            {t('section_title')}
                        </span>
                    </Link>
                    <LanguageToggle />
                </div>
            </header>

            <main className="flex-1">
                <div className="mx-auto max-w-3xl px-4 md:px-6 py-8">{children}</div>
            </main>

            <LegalFooter />
        </div>
    )
}

function LegalFooter() {
    const { t } = useTranslation('legal')

    return (
        <footer className="border-t bg-background">
            <div className="mx-auto max-w-3xl px-4 md:px-6 py-5 space-y-3">
                <nav className="flex flex-wrap gap-x-4 gap-y-2" data-testid="legal-footer-nav">
                    {LEGAL_SLUGS.map((slug) => (
                        <Link
                            key={slug}
                            to="/about/$slug"
                            params={{ slug }}
                            className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                            data-testid={`legal-footer-${slug}`}
                        >
                            {t(`documents.${slug}.title`)}
                        </Link>
                    ))}
                </nav>
                <p className="text-xs text-muted-foreground">
                    {company.name} — {company.address}
                </p>
            </div>
        </footer>
    )
}

/** `/about` — the map of the section. */
export function LegalIndexPage() {
    const { locale } = useLocale()
    const { t } = useTranslation('legal')

    return (
        <LegalLayout>
            <h1 className="text-2xl font-semibold text-foreground">{t('section_title')}</h1>
            <p className="mt-2 text-muted-foreground">
                {t('index.intro', { product: company.product })}
            </p>

            <ul className="mt-6 space-y-3" data-testid="legal-index-list">
                {LEGAL_SLUGS.map((slug) => (
                    <li key={slug}>
                        <Link
                            to="/about/$slug"
                            params={{ slug }}
                            className="block rounded-lg border px-4 py-3 hover:bg-accent transition-colors"
                            data-testid={`legal-index-${slug}`}
                        >
                            <span className="block text-sm font-medium text-foreground">
                                {t(`documents.${slug}.title`)}
                            </span>
                            <span className="block mt-0.5 text-xs text-muted-foreground">
                                {t(`documents.${slug}.summary`, { product: company.product })}
                            </span>
                        </Link>
                    </li>
                ))}
            </ul>

            {/* The spec asks the index to carry the last-updated date: a reader
                sent here by a regulator wants to know how current the set is
                before opening any single document. */}
            <p className="mt-6 text-xs text-muted-foreground" data-testid="legal-index-updated">
                {t('index.updated', { date: legalUpdated(locale) })}
            </p>
        </LegalLayout>
    )
}

/** `/about/{slug}` — one document, rendered from its Markdown source. */
export function LegalDocumentPage({ slug }: { slug: LegalSlug }) {
    const { locale } = useLocale()

    return (
        <LegalLayout>
            <article
                className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-h1:text-2xl prose-h1:mb-6 prose-table:text-xs"
                data-testid={`legal-document-${slug}`}
            >
                <Markdown remarkPlugins={[remarkGfm]}>{legalDocument(slug, locale)}</Markdown>
            </article>
        </LegalLayout>
    )
}
